import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ACTION_ERROR_TITLE } from '../../interceptors/http-error.interceptor';
import type { EduTransactionLog } from '../../models/api/edu-transaction-log.model';
import type { FunTransactionLog } from '../../models/api/fun-transaction-log.model';
import type { Page } from '../../models/api/page.model';
import type { PersonalTravelTransactionLog } from '../../models/api/personal-travel-transaction-log.model';
import type { ActiveUser, CreateUser, EditUser, User } from '../../models/api/user.model';
import type { UsersStatusCount } from '../../models/api/users-status-count.model';
import type { SortQuery } from '../../models/sort-query.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly httpClient = inject(HttpClient);
  private readonly userApi = `${environment.apiUrl}/v1/users`;

  getMe(): Observable<User> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get My User Info');
    return this.httpClient.get<User>(`${this.userApi}/me`, { context });
  }

  getMyActiveYears(): Observable<number[]> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get My Active Years');
    return this.httpClient.get<number[]>(`${this.userApi}/my-active-years`, { context });
  }

  getMyActiveYearsEduEqu(): Observable<number[]> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Active Years Education',
    );
    return this.httpClient.get<number[]>(`${this.userApi}/my-active-years-edu-equ`, { context });
  }

  getMyFunTransactionLog(year: number): Observable<FunTransactionLog> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Fun & Games Transaction Log',
    );
    return this.httpClient.get<FunTransactionLog>(`${this.userApi}/my-transactions-fun`, {
      context,
      params: new HttpParams().set('year', year),
    });
  }

  getMyFunTransactionLogLastTwelveMonths(): Observable<FunTransactionLog> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Fun & Games Transaction Log For Last Twelve Months',
    );
    return this.httpClient.get<FunTransactionLog>(
      `${this.userApi}/my-transactions-fun-last-twelve-months`,
      {
        context,
      },
    );
  }

  getMyPersonalTravelTransactionLog(year: number): Observable<PersonalTravelTransactionLog> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Personal Travel Transaction Log',
    );
    return this.httpClient.get<PersonalTravelTransactionLog>(
      `${this.userApi}/my-transactions-personal-travel`,
      { context, params: new HttpParams().set('year', year) },
    );
  }

  getMyEduTransactionLog(year: number): Observable<EduTransactionLog> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Education Transaction Log',
    );
    return this.httpClient.get<EduTransactionLog>(`${this.userApi}/my-transactions-edu`, {
      context,
      params: new HttpParams().set('year', year),
    });
  }

  getMyEduTransactionLogForCurrentYear(): Observable<EduTransactionLog> {
    const context = new HttpContext().set(
      ACTION_ERROR_TITLE,
      'Failed to Get My Education Transaction Log For Current Year',
    );
    return this.httpClient.get<EduTransactionLog>(
      `${this.userApi}/my-transactions-edu-current-year`,
      {
        context,
      },
    );
  }

  getUsersStatusCount(): Observable<UsersStatusCount> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get Users Status Count');
    return this.httpClient.get<UsersStatusCount>(`${this.userApi}/status-count`, { context });
  }

  getActiveUsers(): Observable<ActiveUser[]> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get Active Users');
    return this.httpClient.get<ActiveUser[]>(`${this.userApi}/active`, { context });
  }

  getUsers(pageNo: number | null, name?: string, sort?: SortQuery[]): Observable<Page<User>> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get Users');

    let params = new HttpParams();

    if (pageNo === null) params = params.set('unpaged', true);
    else params = params.set('page', pageNo).set('size', 10);

    if (name != null) params = params.set('name', name);
    if (sort != null && sort.length > 0)
      params = params.set('sort', sort.map((s) => `${s.property},${s.direction}`).join(','));

    return this.httpClient.get<Page<User>>(this.userApi, { context, params });
  }

  getUser(userId: number): Observable<User> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Get User');
    return this.httpClient.get<User>(`${this.userApi}/${userId}`, { context });
  }

  createUser(user: CreateUser): Observable<User> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Create User');
    return this.httpClient.post<User>(this.userApi, user, { context });
  }

  editUser(userId: number, user: EditUser): Observable<User> {
    const context = new HttpContext().set(ACTION_ERROR_TITLE, 'Failed to Edit User');
    return this.httpClient.patch<User>(`${this.userApi}/${userId}`, user, { context });
  }
}
